import { type FC, type ReactNode, useInsertionEffect } from "react"

import * as styles from "../theme/mode.css"
import {
  installColorScheme,
  installTheme,
  uninstallColorScheme,
  uninstallTheme
} from "../theme/new"
import { themeClass } from "../theme/vars.css"

export interface ThemeProviderProps {
  children?: ReactNode
  mode?: keyof typeof styles.mode | "system"
}

export const ThemeProvider: FC<ThemeProviderProps> = (props) => {
  const { children, mode = "system" } = props

  useInsertionEffect(() => {
    const { classList } = document.body
    classList.add(themeClass)
    return () => classList.remove(themeClass)
  }, [])

  useInsertionEffect(() => {
    const { classList } = document.body
    if (mode === "dark" || mode === "light") {
      const modeClass = styles.mode[mode]
      classList.add(modeClass)
      return () => classList.remove(modeClass)
    } else {
      const mediaQueryList = window.matchMedia("(prefers-color-scheme: dark)")
      const replaceModeClass = (matches: boolean) => {
        classList.add(matches ? styles.mode.dark : styles.mode.light)
        classList.remove(matches ? styles.mode.light : styles.mode.dark)
      }
      replaceModeClass(mediaQueryList.matches)
      const listener = (event: MediaQueryListEvent) => {
        replaceModeClass(event.matches)
      }
      mediaQueryList.addEventListener("change", listener)
      return () => {
        mediaQueryList.removeEventListener("change", listener)
        classList.remove(styles.mode.dark, styles.mode.light)
      }
    }
  }, [mode])
  return <NewThemeProvider mode={mode}>{children}</NewThemeProvider>
}

// This should be renamed ThemeProvider and exported once we got rid of vanilla-extract
function NewThemeProvider(props: ThemeProviderProps) {
  const { children, mode = "system" } = props

  useInsertionEffect(() => {
    installTheme(document.body)
    return () => uninstallTheme(document.body)
  }, [])

  useInsertionEffect(() => {
    if (mode === "dark" || mode === "light") {
      installColorScheme(document.body, mode)
      return () => uninstallColorScheme(document.body)
    }

    function onMediaQueryChange(event: MediaQueryListEvent) {
      replaceModeClass(event.matches)
    }

    function replaceModeClass(matches: boolean) {
      uninstallColorScheme(document.body)
      installColorScheme(document.body, matches ? "dark" : "light")
    }

    const mediaQueryList = window.matchMedia("(prefers-color-scheme: dark)")
    replaceModeClass(mediaQueryList.matches)
    mediaQueryList.addEventListener("change", onMediaQueryChange)

    return () => {
      mediaQueryList.removeEventListener("change", onMediaQueryChange)
      uninstallColorScheme(document.body)
    }
  }, [mode])

  return <>{children}</>
}
